import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import stripTimeZone from 'utils/stripTimeZone'
import cn from 'classnames'
import Loading from 'components/Loading'
import React from 'react'
import StarsRating from 'components/StarsRating'
import { formatNumber } from 'utils/formatNumber'
import Pagination from 'components/Pagination'
import { usePagination } from 'url-pagination'

import {
  CateringRatingsQuery,
  useCateringRatingsQuery
} from './types/CateringRatingsQuery'

const Review = ({
  rating
}: {
  rating: CateringRatingsQuery['cateringRatingsPublic']['records'][0]
}) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const feedback = rating.feedback && rating.feedback.replace(urlRegex, '')

  return (
    <div className="flex md:w-1/2 md:mb-12 mb-8 md:pr-12 pr-4">
      <div>
        <h4 className="text-lg font-bold flex flex-row items-center">
          <span className="mr-2">{rating.name}</span>
          <StarsRating scale={2} stars={rating.stars} />
        </h4>
        <p className="text-gray-500 text-sm font-light">
          {format(parseISO(stripTimeZone(rating.createdAt)), 'd MMMM y')}
        </p>
        {rating.feedback && (
          <p className="mt-1 break-words hyphens-auto" lang="en">
            {feedback}
          </p>
        )}
      </div>
    </div>
  )
}

type Props = {
  categorySlug?: string | null
  foodTypeSlug?: string | null
  marketId?: number | null
  className?: string
}

const CateringRatings = ({
  categorySlug,
  foodTypeSlug,
  marketId,
  className = ''
}: Props) => {
  const pagination = usePagination({ perPage: 10 })

  const { data, error, loading } = useCateringRatingsQuery({
    variables: {
      marketId: marketId ?? null,
      categorySlug: categorySlug ?? null,
      foodTypeSlug: foodTypeSlug ?? null,
      page: {
        limit: pagination.perPage,
        offset: pagination.offset
      }
    }
  })
  if (error) {
    return null
  }

  if (loading) {
    return <Loading className="w-full h-40" />
  }

  const { records, total, average } = data!.cateringRatingsPublic!

  return (
    <div className={cn('pt-12 pb-12 px-4 w-full bg-gray-50', className)}>
      <div className="max-w-4xl mx-auto text-center mb-6 md:mb-10">
        <h2 className="text-3xl font-extrabold sm:text-4xl">
          They talk about us
        </h2>
      </div>
      <div
        className={cn(
          'md:p-0 md:max-w-screen-md lg:max-w-screen-lg max-w-screen-sm mx-auto'
        )}
        id="ratings"
        style={{ scrollMarginTop: '64px' }}
      >
        <div className="flex items-center flex-col md:flex-row mb-10">
          <StarsRating scale={4} stars={5} />
          <h3 className="text-2xl tracking-wide font-semibold ml-4">
            {average} ({formatNumber(total)} reviews)
          </h3>
        </div>
        <div className="flex flex-col md:flex-row flex-wrap">
          {records.map((rating) => (
            <Review key={rating.id} rating={rating} />
          ))}
        </div>
      </div>
      <div className="flex justify-center mt-2">
        <Pagination total={1000} {...pagination} />
      </div>
    </div>
  )
}

export default CateringRatings
